(() => {
  const FLUTTERBLOOM_HANDLE = 'flutterbloom-rechargeable-flapping-butterfly-cat-toy';
  const flutterBloomDiscountCode = (cart) => {
    const quantity = (cart?.items || [])
      .filter((item) => item.handle === FLUTTERBLOOM_HANDLE)
      .reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    if (quantity >= 5) return 'FLUTTER5';
    if (quantity >= 3) return 'FLUTTER3';
    return '';
  };
  const installFlutterBloomCheckoutGuard = () => {
    document.addEventListener('click', async (event) => {
      const checkout = event.target.closest('button[name="checkout"], input[name="checkout"], a[href^="/checkout"]');
      if (!checkout || checkout.dataset.flutterBloomRedirecting === 'true') return;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        const code = flutterBloomDiscountCode(cart);
        if (!code) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        checkout.dataset.flutterBloomRedirecting = 'true';
        window.location.assign(root + 'discount/' + encodeURIComponent(code) + '?redirect=%2Fcheckout');
      } catch (_error) {
        return;
      }
    }, true);
  };

  const GIFT_SELECTOR = 'cart-drawer-gift[data-currency-aware="true"]';
  const CART_ITEMS_SELECTOR = 'cart-drawer-items[data-subtotal]';
  // Every zero-price catalog item is promotional or an internal bundle component.
  // Keep them out of standalone carts while preserving their use beside paid items.
  const GIFT_ONLY_HANDLES = new Set([
    '5-year-pump-warranty',
    'accessory-pack-2-feathers-2-yellow-tails-副本',
    'kitty-shieldz™-cat-scratch-protection-副本',
    'hunting-cover-副本',
    'natural-silvervine-dental-sticks-副本',
    'pet-cleaning-glove',
    'free-speedy-tail-2-0',
    'surprise-electric-cat-toy-free-gift'
  ]);
  let giftOnlyGuardRunning = false;

  const currency = () => window.Shopify?.currency?.active || 'USD';
  const country = () => String(window.Shopify?.country || '').toUpperCase();
  const rate = () => {
    const value = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const marketOverrides = (gift) => {
    const entries = String(gift.dataset.marketRequirements || '')
      .split(/[\n,;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    return new Map(entries.map((entry) => {
      const [key, value] = entry.split('=').map((part) => part.trim());
      return [String(key || '').toUpperCase(), Number(value)];
    }).filter(([key, value]) => key && Number.isFinite(value) && value > 0));
  };

  const formatWholeMoney = (amount) => {
    const value = Math.max(0, Math.ceil(Number(amount) || 0));
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    } catch (_error) {
      return `${currency()} ${value}`;
    }
  };

  const thresholdFor = (gift) => {
    const baseAmount = Number(gift.dataset.baseRequirement);
    if (!Number.isFinite(baseAmount) || baseAmount <= 0) return null;

    const configuredStep = Math.floor(Number(gift.dataset.roundingIncrement));
    const roundingIncrement = Number.isFinite(configuredStep) && configuredStep > 0
      ? configuredStep
      : 100;
    const override = marketOverrides(gift).get(country());
    const convertedAmount = Number.isFinite(override) && override > 0
      ? override
      : baseAmount * rate();
    const wholePresentmentAmount = Math.ceil(convertedAmount / roundingIncrement) * roundingIncrement;
    return {
      major: wholePresentmentAmount,
      cents: wholePresentmentAmount * 100
    };
  };

  const renderProgress = (gift, eligible, subtotalCents, threshold) => {
    const progress = gift.querySelector('.cart-drawer-gift__progress');
    if (!progress) return;

    const template = eligible
      ? gift.dataset.unlockedTemplate
      : gift.dataset.lockedTemplate;
    if (!template) {
      progress.hidden = true;
      return;
    }

    const remainingMajor = Math.ceil(Math.max(0, threshold.cents - subtotalCents) / 100);
    const amount = formatWholeMoney(remainingMajor);
    const renderedText = template.replace('[amount]', amount);
    if (progress.textContent.trim() !== renderedText.trim()) {
      progress.textContent = renderedText;
    }
    progress.hidden = false;
  };

  const patchGift = (gift, subtotalCents) => {
    const threshold = thresholdFor(gift);
    if (!threshold) return;

    const subtotal = Number.isFinite(Number(subtotalCents)) ? Number(subtotalCents) : 0;
    const eligible = subtotal >= threshold.cents;
    gift.dataset.requirement = String(threshold.cents);
    gift.dataset.selected = String(eligible);
    gift.dataset.presentmentRequirement = String(threshold.major);
    gift.dataset.presentmentCurrency = currency();

    renderProgress(gift, eligible, subtotal, threshold);
    gift.querySelectorAll('.cart-drawer-gift__image__locked').forEach((overlay) => {
      overlay.hidden = eligible;
    });
  };

  const patchScope = (scope = document, subtotalOverride) => {
    const cartItems = scope.querySelector?.(CART_ITEMS_SELECTOR);
    const subtotal = subtotalOverride ?? Number(cartItems?.dataset.subtotal || 0);
    scope.querySelectorAll?.(GIFT_SELECTOR).forEach((gift) => patchGift(gift, subtotal));
  };

  const patchSectionHtml = (html, subtotalOverride) => {
    if (!html || typeof html !== 'string') return html;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    patchScope(documentFragment, subtotalOverride);
    return documentFragment.body.innerHTML;
  };

  const installCartDrawerGuard = () => {
    if (!window.customElements?.whenDefined) return;
    customElements.whenDefined('cart-drawer').then(() => {
      const CartDrawer = customElements.get('cart-drawer');
      if (!CartDrawer || CartDrawer.prototype.__currencyAwareGiftInstalled) return;

      const originalRenderContents = CartDrawer.prototype.renderContents;
      if (typeof originalRenderContents !== 'function') return;

      CartDrawer.prototype.renderContents = function renderCurrencyAwareContents(state, dontOpen) {
        if (state?.sections?.['cart-drawer']) {
          const subtotal = Number(state.total_price);
          state.sections['cart-drawer'] = patchSectionHtml(
            state.sections['cart-drawer'],
            Number.isFinite(subtotal) ? subtotal : undefined
          );
        }
        return originalRenderContents.call(this, state, dontOpen);
      };
      CartDrawer.prototype.__currencyAwareGiftInstalled = true;
    });
  };

  const removeStandaloneGifts = async () => {
    if (giftOnlyGuardRunning) return;
    giftOnlyGuardRunning = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      const giftItems = cart.items.filter((item) => GIFT_ONLY_HANDLES.has(item.handle));
      const hasPaidItem = cart.items.some((item) =>
        !GIFT_ONLY_HANDLES.has(item.handle) && Number(item.final_line_price) > 0
      );
      if (!giftItems.length || hasPaidItem) return;

      for (const item of giftItems) {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      }
      window.location.reload();
    } catch (_error) {
      return;
    } finally {
      giftOnlyGuardRunning = false;
    }
  };

  patchScope(document);
  installCartDrawerGuard();
  removeStandaloneGifts();
  installFlutterBloomCheckoutGuard();

  const observer = new MutationObserver((mutations) => {
    const addedElements = mutations.flatMap((mutation) =>
      Array.from(mutation.addedNodes).filter((node) => node.nodeType === Node.ELEMENT_NODE)
    );
    if (!addedElements.length) return;

    const subtotal = Number(document.querySelector(CART_ITEMS_SELECTOR)?.dataset.subtotal || 0);
    addedElements.forEach((element) => {
      if (element.matches?.(GIFT_SELECTOR)) patchGift(element, subtotal);
      element.querySelectorAll?.(GIFT_SELECTOR).forEach((gift) => patchGift(gift, subtotal));
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => patchScope(event.target || document));
  document.addEventListener('cart:updated', () => {
    patchScope(document);
    removeStandaloneGifts();
  });
  document.addEventListener('cart-updated', () => {
    patchScope(document);
    removeStandaloneGifts();
  });
})();
